<template>
	<PostGallery
		v-model:visible="displayBasic"
		v-model:activeIndex="activeIndex"
		:value="galleryItems"
		containerStyle="max-width: 59%"
		circular
		fullScreen
		:showThumbnails="false"
		:showItemNavigators="true"
		@update:activeIndex="handleActiveIndexChange"
	>
		<template #header>
			<div class="text-mb mx-auto px-2 mb-3 font-bold text-white">
				{{ galleryItems?.[activeIndex]?.imageTitle }}
			</div>
		</template>

		<template #item="slotProps">
			<img
				class="mx-auto max-h-[720px]"
				:src="slotProps.item.imageSrc"
				:alt="slotProps.item.imageTitle"
			/>
		</template>

		<template #footer>
			<p class="text-white text-right ml-auto px-2 mt-3 text-xs">
				{{ galleryItems?.[activeIndex]?.imageCopyright }}
			</p>
			<p class="text-white mx-auto px-2 mt-3">{{ galleryItems?.[activeIndex]?.imageDescription }}</p>
		</template>
	</PostGallery>

	<div v-for="(item, activeIndex) in galleryItems" :key="activeIndex" class="border p-3 mb-3 relative">
		<EditButton
			v-if="props.collectionModeData?.editmode"
			class="absolute right-[5px] top-[5px] rounded bg-danger rounded hover:bg-danger-200 items-center p-2 text-center font-sans text-xs font-bold hover:text-secondary-500 transition-all !text-white"
			:id="props.jsonData.identifier ?? ''"
			type="imageattr"
			:overrideURL="item.imageSrcEdit"
		/>

		<div class="text-sm font-semibold mb-3">
			{{ item.imageTitle }}
		</div>
		<div class="mb-3">
			<img
				:src="item.imageSrc"
				class="img-thumbnail w-220px md:w-auto mx-auto text-center overflow-hidden md:h-100% cursor-pointer max-h-[720px]"
				alt="preview"
				@click="openGallery(activeIndex)"
			/>
			<div class="text-sm pt-2">
				<p class="text-right ml-auto mb-2 text-xs">{{ item.imageCopyright }}</p>

				{{ item.imageDescription }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import EditButton from '../components/EditButton.vue';
import type { configCollectionMode } from '@/types'
import { useAppStore } from '@/stores/app-store';

type IllustrationItem = {
	imageTitle?: string;
	imageSrc?: string;
	imageSrcEdit?: string;
	imageCopyright?: string;
	imageDescription?: string;
};

const props = defineProps<{
	jsonData: {
		identifier?: string;
		illustration?: IllustrationItem[];
		_links: {
			illustration: {
				href: string;
			};
		};
	};
	collectionModeData?: configCollectionMode | null;
}>();

const route = useRoute();
const { currentLng } = useAppStore();
const displayBasic = ref(false);
const activeIndex = ref(0);

const getFirstQueryString = (value: unknown): string => {
	if (typeof value === 'string') {
		return value.trim();
	}
	if (Array.isArray(value)) {
		const firstString = value.find((entry) => typeof entry === 'string' && entry.trim().length > 0);
		return typeof firstString === 'string' ? firstString.trim() : '';
	}
	return '';
};

const selectedLng = computed(() => {
	const storeLng = String(currentLng.value ?? '').trim();
	if (storeLng) {
		return storeLng.toLowerCase();
	}
	const routeLng = getFirstQueryString(route.query.lng);
	if (routeLng) {
		return routeLng.toLowerCase();
	}
	return '';
});

const extractLngFromUrl = (rawUrl?: string): string => {
	const urlValue = String(rawUrl ?? '').trim();
	if (!urlValue) {
		return '';
	}
	try {
		const url = new URL(urlValue, window.location.origin);
		return String(url.searchParams.get('lng') ?? '').trim().toLowerCase();
	} catch {
		const matchedLng = urlValue.match(/[?&]lng=([^&#]+)/i)?.[1] ?? '';
		try {
			return decodeURIComponent(matchedLng).trim().toLowerCase();
		} catch {
			return matchedLng.trim().toLowerCase();
		}
	}
};

const galleryItems = computed<IllustrationItem[]>(() => {
	const illustrations = props.jsonData.illustration ?? [];
	const lng = selectedLng.value;
	if (!lng) {
		return [];
	}

	const parsed = illustrations.map((item) => {
		const lngFromEdit = extractLngFromUrl(item.imageSrcEdit);
		const lngFromSrc = extractLngFromUrl(item.imageSrc);
		const itemLng = lngFromEdit || lngFromSrc;
		return { item, itemLng };
	});

	const hasLanguageTaggedItems = parsed.some(({ itemLng }) => Boolean(itemLng));
	if (!hasLanguageTaggedItems) {
		return illustrations;
	}

	const matching = parsed.filter(({ itemLng }) => itemLng === lng).map(({ item }) => item);
	if (matching.length === 0) {
		return [];
	}

	return parsed
		.filter(({ itemLng }) => itemLng === lng || !itemLng)
		.map(({ item }) => item);
});

watch(
	() => galleryItems.value.length,
	(length) => {
		if (length === 0) {
			activeIndex.value = 0;
			displayBasic.value = false;
			return;
		}
		if (activeIndex.value >= length) {
			activeIndex.value = 0;
		}
	}
);

function handleActiveIndexChange(newIndex: number) {
	activeIndex.value = newIndex;
	nextTick(() => {
		console.log('Active Index updated:', activeIndex.value);
	});
}
function openGallery(index: number) {
	activeIndex.value = index;
	displayBasic.value = true;
}
</script>
<style>
button.p-image-preview-indicator:before {
	content: '';
	background: #000;
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	top: 0;
	height: 100%;
	width: 100%;
	opacity: 0.5;
	z-index: 0;
}
button.p-image-preview-indicator svg {
	z-index: 2;
	fill: white;
}
button.p-image-preview-indicator svg path {
	fill: white;
}
.p-image-toolbar {
	background: #000;
}
.p-galleria-mask path {
	fill: white !important;
}
button.p-galleria-item-nav.p-link {
	z-index: 2;
}
.p-galleria-caption {
	padding-top: 10px;
	padding-bottom: 10px;
}
</style>
